import React, { useContext, useEffect, useState } from "react";
import { Button, Grid, Typography } from "@material-ui/core";
import BookmarksIcon from "@material-ui/icons/Bookmarks";
import ExportIcon from "@material-ui/icons/GetAppTwoTone";

import { getCustomerDashboard } from "../api/MeAPI";
import { exportCfdis } from "../api/CustomerCfdiAPI";
import Progress from "../components/Progress";
import { CustomerDashboardResponse } from "../model/Dashboard";
import translate from "../i18n/Translator";
import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import { Cfdi, CfdisExportResponse, CfdiQueryParams } from "../model/Cfdi";
import DateFormat from "../components/DateFormat";
import Surface from "../components/Surface";
import { APP_COLORS } from "../components/Colors";
import CurrencyFormat from "../components/CurrencyFormat";
import moment from "moment";
import CustomerDocumentUploader from "../customers_documents/CustomerDocumentForm";
import { CollectionWidget } from "./CollectionDashboard";
import { AppContext } from "../context/AppContext";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import ExportPopup from "../components/ExportPopup";
import CustomBackdrop from "../components/CustomBackdrop";

export default function CustomerDashboard() {
    const context = useContext(AppContext);
    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<CustomerDashboardResponse>();
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [exportResponse, setExportResponse] = useState<CfdisExportResponse>();
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        setStatus("loading");
        getCustomerDashboard().then((response) => {
            response.cfdis.forEach(cfdi => {
                if (!cfdi.metadata) return;

                const days = cfdi.metadata.credit?.days || 0;
                if (days > 0) {
                    cfdi.metadata.due_date = moment(cfdi.fecha).add(days, "d").toDate();
                } else {
                    cfdi.metadata.due_date = cfdi.fecha;
                }
            });

            response.cfdis.sort((a, b) => {
                if (!a.metadata || !a.metadata.due_date) return -1;
                if (!b.metadata || !b.metadata.due_date) return 1;

                return new Date(a.metadata.due_date).getTime() - new Date(b.metadata.due_date).getTime();
            });

            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    }, []);

    const identifier = (cfdi: Cfdi) => {
        if (cfdi.serie || cfdi.folio) {
            return `${cfdi.serie || ""}${cfdi.serie ? "-" : ""}${cfdi.folio || ""}`;
        }
        if (cfdi.complementos && cfdi.complementos.timbre_fiscal_digital) {
            const uuid = cfdi.complementos.timbre_fiscal_digital.uuid;
            return uuid.substring(uuid.length - 10);
        }
        return "---";
    };

    const showDownloadCfdis = () => {
        setExporting(true);
        exportCfdis(context.session!.tenant!.id, context.session!.customer!.id, {
            search: "",
            type: "I",
            payment_status: "pending"
        } as CfdiQueryParams).then((response) => {
            if (response.url) {
                setExportResponse(response);
            } else {
                setSuccess(translate("cfdis.download.async", { "total": response.total }) as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setExporting(false);
        });
    }

    const onCloseSnackbarks = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    if (status === "loading") {
        return (<Progress />);
    } else if (status !== "loaded" || data === undefined) {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <CustomerDocumentUploader />
            </Grid>
            <Grid item xs={12}>
                <Surface title={translate("dashboard.customer.title") as string} icon={<BookmarksIcon />} avatarColor={APP_COLORS.warning}>
                    <Grid container justify="space-evenly" spacing={3}>
                        <Grid item xs={12} md={8} lg={7}>
                            <Grid container justify="flex-end" spacing={3}>
                                <Grid item xs={12}>
                                    <CollectionWidget collection={data.collection} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Gridable items={data.cfdis}
                                        loading={false}
                                        empty={translate("cfdis.empty") as string}
                                        columns={[
                                            {
                                                title: translate("cfdis.id") as string,
                                                converter: (cfdi) => (
                                                    <div>
                                                        <Ellipsis text={identifier(cfdi)} lenght={100} uppercased={false} /><br />
                                                        <Ellipsis text={translate(`cfdis.tipo_comprobante.${cfdi.tipo_comprobante}`) as string} lenght={0} secondary />
                                                    </div>
                                                ),
                                                xs: true
                                            },
                                            {
                                                title: translate("cfdis.due_date") as string,
                                                converter: (cfdi) => (
                                                    <DateFormat date={cfdi.metadata && cfdi.metadata.due_date ? cfdi.metadata.due_date : cfdi.fecha} format="L" />
                                                ),
                                                xs: 3
                                            },
                                            {
                                                title: translate("cfdis.purchase_order") as string,
                                                converter: (cfdi) => cfdi.metadata?.purchase_order || "---",
                                                xs: 3
                                            },
                                            {
                                                title: translate("cfdis.balance") as string,
                                                converter: (cfdi) => (
                                                    <CurrencyFormat amount={cfdi.metadata?.balance || 0} currency={cfdi.moneda} />
                                                ),
                                                xs: 3
                                            }
                                        ]}
                                    />
                                </Grid>
                                <Grid item xs="auto">
                                    <Button startIcon={<ExportIcon />} onClick={showDownloadCfdis} color="primary" size="small" variant="text">
                                        {translate("buttons.export")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={4} lg={3}>
                            <Grid container justify="center" spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        {translate("dashboard.customer.debt_dollar")}
                                    </Typography>
                                    <Typography variant="h4">
                                        <CurrencyFormat amount={data.debt_dollar || 0} currency="USD" />
                                    </Typography>
                                </Grid>
                                {(data.debt_pesos !== undefined && data.debt_pesos > 0) && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">
                                            {translate("dashboard.customer.debt_pesos")}
                                        </Typography>
                                        <Typography variant="h4">
                                            <CurrencyFormat amount={data.debt_pesos} currency="MXN" />
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        {translate("dashboard.customer.debt_total")}
                                    </Typography>
                                    <Typography variant="h4">
                                        <CurrencyFormat amount={data.debt_total || 0} currency="MXN" />
                                    </Typography>
                                </Grid>
                                {data.dollar !== undefined && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">
                                            {translate("dashboard.customer.exchange_rate_dolar")}
                                        </Typography>
                                        <Typography variant="h6">
                                            <CurrencyFormat amount={data.dollar || 0} currency="MXN" decimals={4} />
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Surface>
            </Grid>
            {exportResponse && exportResponse.url && (
                <ExportPopup
                    title={translate("cfdis.download.title") as string}
                    message={translate("cfdis.download.message", { "total": exportResponse.total }) as string}
                    url={exportResponse.url}
                    onClose={() => setExportResponse(undefined)} />
            )}
            <SuccessSnackbar message={success} onClose={onCloseSnackbarks} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbarks} />
            <CustomBackdrop open={exporting} />
        </Grid>
    );
}
